import React from "react";
import { Box, Typography, Button, CircularProgress, Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CreateTemplateForm from "./CreateTemplateForm";
import theme from "../../utils/theme";
import DimensionTable from "../DimensionTable";
import useComponents from "../../hooks/useComponents";
import DeleteConfirmationPopup from "../DeleteConfirmationPopup";
import PaginationComponent from "../PaginationComponent";

const styles = {
  headerBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: 3,
  },
  title: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  addButton: {
    bgcolor: theme.palette.primary.main,
    "&:hover": {
      bgcolor: theme.palette.primary.dark,
    },
  },
  pagination: {
    position: "sticky",
    bottom: 0,
    backgroundColor: "background.paper",
    py: 1,
    display: "flex",
    justifyContent: "center",
    borderTop: "1px solid",
    borderColor: "divider",
  },
};

const TemplateTable = ({ model, fetchTemplatesAPI, headerCollapsed }) => {
  const {
    components,
    loading,
    openFormDialog,
    currentComponent,
    mode,
    pagination,
    handleEdit,
    handleCreate,
    handleDelete,
    handleCloseDialog,
    handleSubmit,
    deleteModal,
    setDeleteModal,
    setDeleteSelectedComponentId,
    deleteSelectedComponentId,
    handlePageChange,
  } = useComponents(model, fetchTemplatesAPI);

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Box sx={styles.headerBox}>
        <Typography variant="h4" sx={styles.title}>
          {"Template (" + model?.model_name + ")" || "Component Templates"}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={styles.addButton}
        >
          Add Component
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : components?.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No Components available
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCreate}
            sx={{ mt: 2 }}
          >
            Create New Component
          </Button>
        </Box>
      ) : (
        <Grid
          container
          spacing={3}
          sx={{
            minHeight: headerCollapsed ? "65vh" : "50vh",
            maxHeight: headerCollapsed ? "65vh" : "50vh",
            overflowY: "auto",
          }}
        >
          {components?.map((component) => (
            <Grid item size={{ xs: 12 }} key={component.component_id}>
              <DimensionTable
                items={component.items}
                component_name={component.component_name}
                images={component.component_images}
                onEdit={() => handleEdit(component)}
                onDelete={() => {
                  setDeleteSelectedComponentId(component);
                  setDeleteModal(true);
                }}
                disableDelete={components.length === 1}
              />
            </Grid>
          ))}
        </Grid>
      )}
      {pagination.total > pagination.perPage && (
        <PaginationComponent
          pagination={pagination}
          handlePageChange={handlePageChange}
        />
      )}

      <DeleteConfirmationPopup
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        itemName={deleteSelectedComponentId?.component_name || ""}
      />

      {/* Form Dialog */}
      <CreateTemplateForm
        open={openFormDialog}
        onClose={handleCloseDialog}
        onSave={handleSubmit}
        initialData={currentComponent}
        isEditing={mode === "edit"}
        title="Create Component"
      />
    </Box>
  );
};

export default TemplateTable;
